import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import PFSAdd from './PFSadd';
import PFSEdit from './PFSedit';
import ExcelJS from 'exceljs';

const FUND_DATA = [
  {
    source: 'DA Central',
    year: '2022',
    amount: '₱5,000,000',
    dateStarted: '2022-02-15',
    dateCompleted: '2022-12-10',
    turnoverDate: '2023-01-05'
  },
  {
    source: 'World Bank',
    year: '2023',
    amount: '₱10,000,000',
    dateStarted: '2023-03-20',
    dateCompleted: '2023-11-25',
    turnoverDate: '2024-01-10'
  },
];

const PFS = ({ onBack }) => {
  const [activeForm, setActiveForm] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);

  const openAddForm = () => {
    setActiveForm('add');
    setSelectedRow(null);
  };

  const openEditForm = (item) => {
    setSelectedRow(item);
    setActiveForm('edit');
  };

  const closeForm = () => {
    setActiveForm(null);
    setSelectedRow(null);
  };

  const exportToExcel = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('FundSources');

    // Set page orientation to landscape
    worksheet.pageSetup.orientation = 'landscape';

    // Title row
    worksheet.mergeCells('A1:G1');
    const titleCell = worksheet.getCell('A1');
    titleCell.value = 'PROJECT FUNDING SOURCES';
    titleCell.font = { name: 'Calibri', size: 20, bold: true };
    titleCell.alignment = { horizontal: 'left', vertical: 'left' };

    // Empty row
    worksheet.addRow([]);

    // Header row
    const headerRow = [
      'No', 'Fund Source', 'Fund Year', 'Fund Amount', 'Date Started', 'Date Completed', 'Turnover Date'
    ];
    worksheet.addRow(headerRow);

    // Data rows
    FUND_DATA.forEach((item, index) => {
      worksheet.addRow([
        index + 1,
        item.source,
        item.year,
        item.amount,
        item.dateStarted,
        item.dateCompleted,
        item.turnoverDate,
      ]);
    });

    // Set column widths and alignment
    worksheet.columns = [
      { width: 5, alignment: { horizontal: 'left' } }, // No
      { width: 22, alignment: { horizontal: 'left' } }, // Fund Source
      { width: 12, alignment: { horizontal: 'left' } }, // Fund Year
      { width: 18, alignment: { horizontal: 'left' } }, // Fund Amount
      { width: 17, alignment: { horizontal: 'left' } }, // Date Started
      { width: 20, alignment: { horizontal: 'left' } }, // Date Completed
      { width: 17, alignment: { horizontal: 'left' } }, // Turnover Date
    ];

    // Style header row
    const headerRowNumber = 3;
    worksheet.getRow(headerRowNumber).eachCell(cell => {
      cell.font = { name: 'Calibri', size: 11, color: { argb: 'FFFFFFFF' } };
      cell.alignment = { horizontal: 'left', vertical: 'middle' };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '2E7D32' } };
      cell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' },
      };
    });

    // Style data rows
    for (let i = headerRowNumber + 1; i <= worksheet.rowCount; i++) {
      worksheet.getRow(i).eachCell((cell, colNumber) => {
        cell.font = { name: 'Calibri', size: 11, color: { argb: '00000000' } };
        cell.alignment = { horizontal: 'left', vertical: 'middle' };
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' },
        };
      });
    }

    // Set title row height
    worksheet.getRow(1).height = 35;

    // Download file with file picker
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

    if (window.isSecureContext && 'showSaveFilePicker' in window) {
      try {
        const options = {
          suggestedName: 'FundSources.xlsx',
          types: [{
            description: 'Excel File',
            accept: { 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'] }
          }]
        };
        const handle = await window.showSaveFilePicker(options);
        const writable = await handle.createWritable();
        await writable.write(blob);
        await writable.close();
        setShowSuccess(true);
      } catch (err) {
        console.error('Error saving file:', err.name, err.message || err);
        if (err.name !== 'AbortError') {
          alert('Save failed due to an unexpected error. Check the console for details.');
        }
      }
    } else {
      // Fallback to original download method
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'FundSources.xlsx';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      setShowSuccess(true);
    }
  };

  useEffect(() => {
    if (showSuccess) {
      const timer = setTimeout(() => setShowSuccess(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [showSuccess]);

  return (
    <Container>
      <BackButton onClick={onBack}>‹</BackButton>

      <Header>
        <Title>PROJECT FUNDING SOURCE</Title>
        <Actions>
          <SearchInput type="text" placeholder="Search..." />
          <Button onClick={openAddForm}>Add Fund Source</Button>
          <Button onClick={exportToExcel}>Export</Button>
        </Actions>
      </Header>

      <Content>
        {showSuccess && (
          <SuccessMessageContainer>
            <SuccessMessageText>File saved successfully!</SuccessMessageText>
          </SuccessMessageContainer>
        )}
        <TableWrapper>
          <StyledTable>
            <thead>
              <tr>
                <th>No</th>
                <th>Fund Source</th>
                <th>Fund Year</th>
                <th>Fund Amount</th>
                <th>Date Started</th>
                <th>Date Completed</th>
                <th>Turnover Date</th>
              </tr>
            </thead>
            <tbody>
              {FUND_DATA.map((item, index) => (
                <tr key={index} onClick={() => openEditForm(item)}>
                  <td>{index + 1}</td>
                  <td>{item.source}</td>
                  <td>{item.year}</td>
                  <td>{item.amount}</td>
                  <td>{item.dateStarted}</td>
                  <td>{item.dateCompleted}</td>
                  <td>{item.turnoverDate}</td>
                </tr>
              ))}
            </tbody>
          </StyledTable>
        </TableWrapper>

        {activeForm === 'add' && <PFSAdd onClose={closeForm} />}
             {activeForm === 'edit' && selectedRow && (
                  <PFSEdit data={selectedRow} onClose={closeForm} />
                )}
      </Content>
    </Container>
  );
};

export default PFS;

// Styled components
const Container = styled.div`
  padding: 80px 20px 20px;
  font-family: sans-serif;
  min-height: 100vh;
  box-sizing: border-box;
`;

const Content = styled.div`
  display: flex;
  gap: 20px;
`;

const TableWrapper = styled.div`
  width: 100%;
  overflow-x: auto;
`;

const BackButton = styled.button`
  position: absolute;
  margin-bottom: 70px;
  top: 50px;
  left: 85px;
  background: transparent;
  border: none;
  font-size: 28px;
  font-weight: 900;
  cursor: pointer;
  color: #2e7d32;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
`;

const Title = styled.h2`
  font-size: 18px;
  font-weight: bold;
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const SearchInput = styled.input`
  padding: 6px 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
`;

const Button = styled.button`
  padding: 6px 10px;
  background-color: #4caf50;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;

  &:hover {
    background-color: #388e3c;
  }
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;

  th {
    background-color: #2e7d32;
    color: white;
    padding: 10px;
    font-size: 14px;
  }

  td {
    padding: 10px;
    font-size: 14px;
    border: 1px solid #ddd;
  }

  tbody tr:nth-child(even) {
    background-color: #f9f9f9;
  }

  tbody tr:hover {
    background-color: #f1f1f1;
  }
`;

const SuccessMessageContainer = styled.div`
  position: absolute;
  bottom: 20px;
  right: 0;
  width: 20%;
  background-color: #4CAF50;
  color: #ffffff;
  padding: 10px 20px;
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  z-index: 1000;
  animation: slideIn 0.3s ease-in, slideOut 0.3s ease-in 2.7s;
  @keyframes slideIn {
    from { transform: translateX(100%); }
    to { transform: translateX(0); }
  }
  @keyframes slideOut {
    from { transform: translateX(0); }
    to { transform: translateX(100%); }
  }
`;

const SuccessMessageText = styled.p`
  margin: 0;
  font-size: 16px;
  font-family: Arial, sans-serif;
`;
